import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send,
  Upload,
  CheckCircle2,
  AlertCircle,
  FileText,
  Paperclip,
  X as XIcon,
} from 'lucide-react';
import {
  Container,
  Button,
  OtpModal,
  PhoneInput,
  MessageAssistToolbar,
  composePhoneNumber,
  DEFAULT_PHONE_VALUE,
  type PhoneValue,
} from '../ui';
import { cn } from '../../utils/cn';
import { FormPrivacyNotice } from './FormPrivacyNotice';
import { sendOtp, validateOtpAndSubmit } from '../../services/contactApi';
import {
  submitRfp,
  validateRfpDocument,
  MAX_RFP_BYTES,
  RFP_ACCEPT_ATTR,
} from '../../services/rfpApi';
import { INDUSTRIES } from '../../data/industries';

/*
 * ConnectSection
 * --------------
 * Global "Let's Connect" lead-capture strip rendered just above the
 * site footer on every page except /contact.
 *
 * Design intent (unchanged):
 *   - Full-bleed navy background (#051124 family) that matches the
 *     Contact-page contact form so the two surfaces read as one
 *     design language across the site.
 *   - Slowly-drifting blue/indigo orbs for depth.
 *   - No card wrapper — inputs float on the navy so the white pills
 *     read as the primary UI.
 *
 * Feature parity with the Contact page (as of this pass):
 *   ✓ Tab switcher: "Contact us" ↔ "Submit an RFP" with slide-in
 *     animation between panels
 *   ✓ RFP form with multi-select relationship chips + PDF file upload
 *     (drag-and-drop or Browse)
 *   ✓ MessageAssistToolbar on the textarea (mic dictation + AI polish)
 *   ✓ Phone-country auto-detection (inherited automatically from the
 *     shared <PhoneInput> component)
 *   ✓ Height-stable AnimatePresence so switching tabs doesn't cause
 *     the enclosing section to jump vertically
 *
 * The only difference from the Contact page is layout chrome — this
 * section stays borderless / edge-to-edge instead of sitting inside a
 * white Card.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

// Same list used on the Contact page so admin notifications stay
// consistent (their labels drive `resolveRelationshipLabel`).
const RELATIONSHIP_OPTIONS = [
  { id: 'client-prospect', label: 'Client / Prospect' },
  { id: 'partner-alliance', label: 'Partner / Alliance' },
  { id: 'investor-analyst', label: 'Investor / Analyst' },
  { id: 'media-journalist', label: 'Media / Journalist' },
  { id: 'others', label: 'Others' },
] as const;

// RFP mode pre-selects these two chips because those are the two most
// common intents behind an RFP submission (buyer + partner). Users can
// toggle either chip off before submitting.
const DEFAULT_RFP_RELATIONSHIP_IDS = ['client-prospect', 'partner-alliance'];

// Industry dropdown — same catalog as the /contact page's dropdown so
// both surfaces stay in lock-step. Derived from the shared
// `INDUSTRIES` module (which also drives the sidebar mega-menu, the
// /industries listing, and every /industries/<slug> detail page).
const INDUSTRY_OPTIONS = [
  ...INDUSTRIES.map((ind) => ({ id: ind.id, label: ind.title })),
  { id: 'other-not-sure', label: 'Other / Not Sure' },
] as const;

type FormMode = 'contact' | 'rfp';

interface ConnectFormState {
  name: string;
  workEmail: string;
  company: string;
  phone: PhoneValue;
  relationship: string;
  /**
   * Industry vertical the visitor operates in. Optional — empty falls
   * back to "NA" server-side. Shared between the Contact-us and RFP
   * tabs so a mid-form tab-switch doesn't wipe the selection.
   */
  industry: string;
  message: string;
}

const INITIAL_FORM: ConnectFormState = {
  name: '',
  workEmail: '',
  company: '',
  phone: DEFAULT_PHONE_VALUE,
  relationship: '',
  industry: '',
  message: '',
};

function resolveRelationshipLabel(state: ConnectFormState): string {
  const match = RELATIONSHIP_OPTIONS.find((o) => o.id === state.relationship);
  return match?.label || state.relationship || 'NA';
}

function resolveRfpRelationshipLabels(ids: readonly string[]): string {
  if (ids.length === 0) return 'NA';
  const labels: string[] = [];
  for (const id of ids) {
    const match = RELATIONSHIP_OPTIONS.find((o) => o.id === id);
    if (match) labels.push(match.label);
  }
  return labels.length > 0 ? labels.join(', ') : 'NA';
}

function resolveIndustryLabel(industryId: string): string {
  if (!industryId) return 'NA';
  const match = INDUSTRY_OPTIONS.find((i) => i.id === industryId);
  return match?.label || industryId;
}

// AnimatePresence slide variants — direction-aware so switching Contact
// → RFP glides in from the right, and RFP → Contact glides in from the
// left. `position: absolute` on `exit` lets the incoming and outgoing
// panels overlap without pushing layout mid-animation.
const slideVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0,
    position: 'absolute' as const,
    top: 0,
    left: 0,
    right: 0,
  }),
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ConnectSection: React.FC = () => {
  // ---- Shared form state -------------------------------------------------
  const [formData, setFormData] = useState<ConnectFormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submittedMode, setSubmittedMode] = useState<FormMode>('contact');

  // ---- OTP state ---------------------------------------------------------
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpVerifyError, setOtpVerifyError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  // ---- Mode state --------------------------------------------------------
  const [formMode, setFormMode] = useState<FormMode>('contact');
  // Slide direction for the AnimatePresence variants. +1 = new panel
  // enters from the right, -1 = enters from the left.
  const slideDirRef = useRef<1 | -1>(1);

  // ---- RFP-only state ----------------------------------------------------
  const [rfpRelationships, setRfpRelationships] = useState<string[]>([
    ...DEFAULT_RFP_RELATIONSHIP_IDS,
  ]);
  const [rfpFile, setRfpFile] = useState<File | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);

  // ---- Height stability across tab switches ------------------------------
  // The RFP form is taller than the contact form (extra file uploader).
  // Left to itself, the enclosing navy section would grow/shrink each
  // switch, which reads as jitter and pushes the footer around. We pin
  // the outer form wrapper to the max height we've ever observed so
  // once we've rendered the taller form once, switching back never
  // shrinks. Grow-only — mirrors the pattern in Contact.tsx.
  const [minFormHeight, setMinFormHeight] = useState(0);
  const formHeightObserverRef = useRef<ResizeObserver | null>(null);

  const attachFormMeasurement = useCallback((el: HTMLDivElement | null) => {
    formHeightObserverRef.current?.disconnect();
    formHeightObserverRef.current = null;
    if (!el || typeof ResizeObserver === 'undefined') return;

    const measure = (h: number) => {
      const ceil = Math.ceil(h);
      setMinFormHeight((prev) => (ceil > prev ? ceil : prev));
    };
    measure(el.offsetHeight);

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) measure(entry.contentRect.height);
    });
    ro.observe(el);
    formHeightObserverRef.current = ro;
  }, []);

  useEffect(() => {
    return () => {
      formHeightObserverRef.current?.disconnect();
      formHeightObserverRef.current = null;
    };
  }, []);

  // ---- Handlers: shared --------------------------------------------------
  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const handlePhoneChange = (value: PhoneValue) => {
    setFormData((prev) => ({ ...prev, phone: value }));
    if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
  };

  // Passed to MessageAssistToolbar so mic dictation + AI polish can
  // write straight into `formData.message`. Stable callback because
  // the toolbar stashes it in a ref.
  const setMessageValue = useCallback((next: string) => {
    setFormData((prev) => ({ ...prev, message: next }));
    setErrors((prev) => (prev.message ? { ...prev, message: '' } : prev));
  }, []);

  const handleModeSwitch = (next: FormMode) => {
    if (next === formMode) return;
    slideDirRef.current = next === 'rfp' ? 1 : -1;
    setFormMode(next);
    // Wipe field errors so switching tabs feels like a fresh slate —
    // but keep the shared field values so the user isn't punished
    // for exploring both flows.
    setErrors({});
    setApiError('');
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setApiError('');
    setOtpVerifyError('');
    setRfpRelationships([...DEFAULT_RFP_RELATIONSHIP_IDS]);
    setRfpFile(null);
    setIsSubmitted(false);
    setSubmittedMode('contact');
  };

  // ---- Handlers: contact mode --------------------------------------------
  const validateContactForm = (): boolean => {
    const next: Record<string, string> = {};

    if (!formData.name.trim()) next.name = 'Name is required';

    if (!formData.workEmail.trim()) {
      next.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      next.workEmail = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) next.company = 'Company is required';

    if (!formData.phone.number.trim()) {
      next.phone = 'Phone number is required';
    } else if (!/^[\d\s-]{6,}$/.test(formData.phone.number.trim())) {
      next.phone = 'Please enter a valid phone number';
    }

    if (!formData.relationship) {
      next.relationship = 'Please select your relationship with us';
    }

    if (!formData.message.trim()) {
      next.message = 'Please add a short message';
    } else if (formData.message.trim().length < 10) {
      next.message = 'Please provide a little more detail (at least 10 characters)';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (!validateContactForm()) return;

    setIsSubmitting(true);
    try {
      const response = await sendOtp({
        name: formData.name.trim(),
        email: formData.workEmail.trim(),
        require_work_email: true,
      });

      if (response.status === 'success') {
        setShowOtpModal(true);
      } else if (response.status_code === 400) {
        setErrors((prev) => ({
          ...prev,
          workEmail:
            response.message ||
            'Please use a work email address. Personal providers (gmail, hotmail, yahoo…) are not accepted.',
        }));
      } else if (response.status_code === 429) {
        const retryAfter = (response.data as { retry_after_seconds?: number } | null)?.retry_after_seconds;
        setApiError(
          retryAfter
            ? `Please wait ${retryAfter} seconds before requesting another OTP.`
            : response.message,
        );
      } else {
        setApiError(response.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactOtpVerify = useCallback(
    async (otp: string) => {
      setOtpVerifyError('');
      setIsVerifyingOtp(true);
      try {
        const response = await validateOtpAndSubmit({
          name: formData.name.trim(),
          work_email: formData.workEmail.trim(),
          personal_email: 'na@gmail.com',
          company: formData.company.trim(),
          phone_number: composePhoneNumber(formData.phone),
          // The global CTA doesn't offer a service picker — the
          // admin email shows "General Inquiry" so support triage
          // can still route it.
          service_interested_in: 'General Inquiry',
          relationship: resolveRelationshipLabel(formData),
          industry: resolveIndustryLabel(formData.industry),
          message: formData.message.trim(),
          otp,
        });

        if (response.status === 'success') {
          setShowOtpModal(false);
          setSubmittedMode('contact');
          setIsSubmitted(true);
        } else {
          setOtpVerifyError(response.message || 'Verification failed. Please try again.');
        }
      } finally {
        setIsVerifyingOtp(false);
      }
    },
    [formData],
  );

  // ---- Handlers: RFP mode ------------------------------------------------
  const toggleRfpRelationship = (id: string) => {
    setRfpRelationships((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
    if (errors.rfpRelationships) {
      setErrors((prev) => ({ ...prev, rfpRelationships: '' }));
    }
  };

  const acceptRfpFile = (file: File | null) => {
    // Commit the file even if validation fails so the user can see
    // what they tried to upload — the error message below tells them
    // why it was rejected.
    setRfpFile(file);
    if (!file) {
      if (errors.rfpFile) {
        setErrors((prev) => ({ ...prev, rfpFile: '' }));
      }
      return;
    }
    const validation = validateRfpDocument(file);
    if (validation.ok) {
      if (errors.rfpFile) {
        setErrors((prev) => ({ ...prev, rfpFile: '' }));
      }
    } else {
      setErrors((prev) => ({
        ...prev,
        rfpFile: validation.message || 'Please attach a valid PDF (max 5 MB).',
      }));
    }
  };

  const handleRfpFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    acceptRfpFile(file);
    // Clear the input so re-uploading the same file after removing
    // still fires onChange.
    e.target.value = '';
  };

  const handleRfpFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingFile(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    acceptRfpFile(file);
  };

  const handleRemoveRfpFile = () => {
    setRfpFile(null);
    if (errors.rfpFile) {
      setErrors((prev) => ({ ...prev, rfpFile: '' }));
    }
  };

  const validateRfpForm = (): boolean => {
    const next: Record<string, string> = {};

    if (!formData.name.trim()) next.name = 'Name is required';

    if (!formData.workEmail.trim()) {
      next.workEmail = 'Work email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.workEmail)) {
      next.workEmail = 'Please enter a valid email address';
    }

    if (!formData.company.trim()) next.company = 'Company is required';

    if (!formData.phone.number.trim()) {
      next.phone = 'Phone number is required';
    } else if (!/^[\d\s-]{6,}$/.test(formData.phone.number.trim())) {
      next.phone = 'Please enter a valid phone number';
    }

    if (rfpRelationships.length === 0) {
      next.rfpRelationships = 'Please select at least one relationship';
    }

    if (!rfpFile) {
      next.rfpFile = 'Please attach your RFP/RFI document';
    } else {
      const validation = validateRfpDocument(rfpFile);
      if (!validation.ok) {
        next.rfpFile =
          validation.message || 'Please attach a valid PDF (max 5 MB).';
      }
    }

    if (!formData.message.trim()) {
      next.message = 'Please add a short message';
    } else if (formData.message.trim().length < 10) {
      next.message = 'Please provide a little more detail (at least 10 characters)';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleRfpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    if (!validateRfpForm()) return;

    setIsSubmitting(true);
    try {
      const response = await sendOtp({
        name: formData.name.trim(),
        email: formData.workEmail.trim(),
        require_work_email: true,
      });

      if (response.status === 'success') {
        setShowOtpModal(true);
      } else if (response.status_code === 400) {
        setErrors((prev) => ({
          ...prev,
          workEmail:
            response.message ||
            'Please use a work email address. Personal providers (gmail, hotmail, yahoo…) are not accepted.',
        }));
      } else if (response.status_code === 429) {
        const retryAfter = (response.data as { retry_after_seconds?: number } | null)?.retry_after_seconds;
        setApiError(
          retryAfter
            ? `Please wait ${retryAfter} seconds before requesting another OTP.`
            : response.message,
        );
      } else {
        setApiError(response.message || 'Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRfpOtpVerify = useCallback(
    async (otp: string) => {
      setOtpVerifyError('');
      setIsVerifyingOtp(true);
      try {
        if (!rfpFile) {
          setOtpVerifyError(
            'Please attach your RFP document and try again.',
          );
          return;
        }
        const response = await submitRfp({
          name: formData.name.trim(),
          work_email: formData.workEmail.trim(),
          personal_email: 'na@gmail.com',
          company: formData.company.trim(),
          phone_number: composePhoneNumber(formData.phone),
          relationship: resolveRfpRelationshipLabels(rfpRelationships),
          industry: resolveIndustryLabel(formData.industry),
          message: formData.message.trim(),
          otp,
          rfp_document: rfpFile,
        });

        if (response.status === 'success') {
          setShowOtpModal(false);
          setSubmittedMode('rfp');
          setIsSubmitted(true);
        } else {
          setOtpVerifyError(
            response.message || 'Verification failed. Please try again.',
          );
        }
      } finally {
        setIsVerifyingOtp(false);
      }
    },
    [formData, rfpFile, rfpRelationships],
  );

  const handleResendOtp = useCallback(async () => {
    const response = await sendOtp({
      name: formData.name.trim(),
      email: formData.workEmail.trim(),
      require_work_email: true,
    });
    if (response.status === 'success') return { success: true };
    if (response.status_code === 429) {
      const retryAfter = (response.data as { retry_after_seconds?: number } | null)?.retry_after_seconds;
      setOtpVerifyError(response.message);
      return { success: false, retryAfter: retryAfter || 60 };
    }
    setOtpVerifyError(response.message || 'Failed to resend OTP.');
    return { success: false };
  }, [formData.name, formData.workEmail]);

  // ---- Shared input styling ---------------------------------------------
  const baseInput =
    'w-full px-4 py-3.5 rounded-lg border bg-white text-navy-900 placeholder:text-navy-400 outline-none transition-all focus:ring-2';
  const okBorder =
    'border-white hover:border-navy-200 focus:border-accent-500 focus:ring-accent-400/40';
  const errBorder =
    'border-red-400 focus:border-red-500 focus:ring-red-400/40';
  const inputCls = (err?: string) => `${baseInput} ${err ? errBorder : okBorder}`;
  const optionCls = 'bg-white text-navy-900';
  const errText =
    'mt-1.5 text-xs text-red-200 flex items-center gap-1';

  // Which key AnimatePresence should observe. Once submitted we swap to
  // a stable "success" key so the exit-out of whichever form was
  // active and enter of the success card animate cleanly.
  const activeKey = isSubmitted ? 'success' : formMode;
  const activeMode: FormMode | 'success' = isSubmitted ? 'success' : formMode;

  return (
    <>
      {/* OTP verification modal — verify handler picks between contact
          and RFP submission based on the mode the user last submitted
          from. The email address is shared. */}
      <OtpModal
        isOpen={showOtpModal}
        email={formData.workEmail}
        onClose={() => {
          setShowOtpModal(false);
          setOtpVerifyError('');
        }}
        onVerify={
          formMode === 'rfp' ? handleRfpOtpVerify : handleContactOtpVerify
        }
        onResend={handleResendOtp}
        verifyError={otpVerifyError}
        isVerifying={isVerifyingOtp}
      />

      {/* Full-bleed navy section — same gradient / orbs as the Contact
          form area so the two surfaces read as one design language. */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          background:
            'linear-gradient(135deg, #020815 0%, #051124 35%, #0a2148 65%, #071730 100%)',
        }}
      >
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full blur-3xl bg-blue-600/25"
          animate={{ x: [0, 90, 0], y: [0, 70, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute -bottom-48 -right-32 w-[600px] h-[600px] rounded-full blur-3xl bg-indigo-600/25"
          animate={{ x: [0, -80, 0], y: [0, -60, 0] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden
          className="pointer-events-none absolute top-1/3 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full blur-3xl bg-sky-600/15"
          animate={{ x: [-60, 60, -60], y: [40, -40, 40] }}
          transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
        />

        <Container className="relative z-10 py-16 md:py-20">
          <h2 className="heading-xl mb-6 md:mb-8">Let&rsquo;s Connect</h2>

          {/* Tab switcher — hidden on the success screen so the user
              isn't confused into thinking they can still submit. */}
          {!isSubmitted && (
            <div className="flex flex-wrap gap-3 mb-6 md:mb-8">
              {(
                [
                  { id: 'contact' as const, label: 'Contact us' },
                  { id: 'rfp' as const, label: 'Submit an RFP' },
                ]
              ).map((tab) => {
                const active = formMode === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => handleModeSwitch(tab.id)}
                    aria-pressed={active}
                    className={cn(
                      'px-5 py-2.5 rounded-full text-sm font-semibold transition-all border',
                      active
                        ? 'bg-white text-navy-950 border-white shadow-lg'
                        : 'bg-white/5 text-white/85 border-white/25 hover:bg-white/10 hover:border-white/40',
                    )}
                  >
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Sliding container. `minHeight` is grow-only via the
              ResizeObserver above so switching between the shorter
              contact form and the taller RFP form never shrinks the
              enclosing section — no jump in the surrounding layout. */}
          <div
            className="relative"
            style={
              minFormHeight ? { minHeight: `${minFormHeight}px` } : undefined
            }
          >
            <AnimatePresence
              mode="wait"
              custom={slideDirRef.current}
              initial={false}
            >
              <motion.div
                key={activeKey}
                ref={attachFormMeasurement}
                custom={slideDirRef.current}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="w-full"
              >
                {activeMode === 'success' ? (
                  // ═══════════ Success card ═══════════
                  <div className="max-w-2xl">
                    <div className="flex items-start gap-4 p-6 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm">
                      <div className="w-11 h-11 rounded-full bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-6 h-6 text-emerald-300" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-white mb-1">
                          {submittedMode === 'rfp'
                            ? 'RFP received — thank you.'
                            : 'Thanks — your message is on its way.'}
                        </h3>
                        <p className="text-sm text-white/70 mb-4">
                          {submittedMode === 'rfp'
                            ? "We've got your document and details. Our team will review and get back to you within 24 hours."
                            : 'Our team typically responds within 24 hours.'}
                        </p>
                        <button
                          type="button"
                          onClick={resetForm}
                          className="text-sm font-semibold text-white underline decoration-white/30 underline-offset-4 hover:decoration-white transition-colors"
                        >
                          Send another message
                        </button>
                      </div>
                    </div>
                  </div>
                ) : activeMode === 'contact' ? (
                  // ═══════════ CONTACT-US form ═══════════
                  <form onSubmit={handleContactSubmit} noValidate>
                    {apiError && (
                      <motion.div
                        className="mb-6 flex items-center gap-2 p-3 rounded-lg bg-red-500/15 border border-red-400/40"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <AlertCircle className="w-4 h-4 text-red-200 flex-shrink-0" />
                        <p className="text-sm text-red-100">{apiError}</p>
                      </motion.div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      {/* Row 1: Relationship + Work email */}
                      <div>
                        <label htmlFor="connect-relationship" className="sr-only">
                          Relationship
                        </label>
                        <select
                          id="connect-relationship"
                          name="relationship"
                          value={formData.relationship}
                          onChange={handleChange}
                          className={`${inputCls(errors.relationship)} appearance-none`}
                          aria-invalid={Boolean(errors.relationship)}
                        >
                          <option value="" className={optionCls}>
                            What&rsquo;s your relationship with us?
                          </option>
                          {RELATIONSHIP_OPTIONS.map((opt) => (
                            <option key={opt.id} value={opt.id} className={optionCls}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        {errors.relationship && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.relationship}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="connect-workEmail" className="sr-only">
                          Work email
                        </label>
                        <input
                          type="email"
                          id="connect-workEmail"
                          name="workEmail"
                          value={formData.workEmail}
                          onChange={handleChange}
                          placeholder="Work email address"
                          className={inputCls(errors.workEmail)}
                          aria-invalid={Boolean(errors.workEmail)}
                        />
                        {errors.workEmail && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.workEmail}
                          </p>
                        )}
                      </div>

                      {/* Row 2: Full name + Phone */}
                      <div>
                        <label htmlFor="connect-name" className="sr-only">
                          Full name
                        </label>
                        <input
                          type="text"
                          id="connect-name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Full name"
                          className={inputCls(errors.name)}
                          aria-invalid={Boolean(errors.name)}
                        />
                        {errors.name && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <PhoneInput
                          id="connect-phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          hasError={Boolean(errors.phone)}
                        />
                        {errors.phone && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.phone}
                          </p>
                        )}
                      </div>

                      {/* Row 3: Company (full width) */}
                      <div className="md:col-span-2">
                        <label htmlFor="connect-company" className="sr-only">
                          Company
                        </label>
                        <input
                          type="text"
                          id="connect-company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Company name"
                          className={inputCls(errors.company)}
                          aria-invalid={Boolean(errors.company)}
                        />
                        {errors.company && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.company}
                          </p>
                        )}
                      </div>

                      {/* Row 4: Industry (full width, optional) — kept
                          on its own row because the option labels are
                          long and would truncate awkwardly in a
                          half-width column. */}
                      <div className="md:col-span-2">
                        <label htmlFor="connect-industry" className="sr-only">
                          Industry
                        </label>
                        <select
                          id="connect-industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          className={`${inputCls(errors.industry)} appearance-none`}
                        >
                          <option value="" className={optionCls}>
                            Which industry are you in? (optional)
                          </option>
                          {INDUSTRY_OPTIONS.map((ind) => (
                            <option key={ind.id} value={ind.id} className={optionCls}>
                              {ind.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Row 5: Message (with mic + AI polish toolbar) */}
                      <div className="md:col-span-2">
                        <label htmlFor="connect-message" className="sr-only">
                          Message
                        </label>
                        <MessageAssistToolbar
                          value={formData.message}
                          onChange={setMessageValue}
                          context="contact"
                          disabled={isSubmitting}
                        >
                          <textarea
                            id="connect-message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={5}
                            placeholder="Tell us what you're building — we'll take it from there."
                            className={`${inputCls(errors.message)} resize-none`}
                            // Reserve room at the top-right for the
                            // mic + AI-polish icons so typed text
                            // doesn't slide under them.
                            style={{ paddingRight: '6rem' }}
                            aria-invalid={Boolean(errors.message)}
                          />
                        </MessageAssistToolbar>
                        {errors.message && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <p className="text-xs text-white/55">
                        We&rsquo;ll send a 4-digit code to your work email to
                        confirm your identity. Personal-provider emails (gmail,
                        yahoo…) aren&rsquo;t accepted.
                      </p>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        isLoading={isSubmitting}
                        rightIcon={<Send className="w-5 h-5" />}
                        className="md:w-auto w-full"
                      >
                        Submit
                      </Button>
                    </div>

                    {/* Privacy / compliance reassurance — anchored
                        to the bottom of the form so it's the last
                        thing a visitor reads before hitting submit. */}
                    <FormPrivacyNotice tone="onDark" className="mt-6" />
                  </form>
                ) : (
                  // ═══════════ SUBMIT-AN-RFP form ═══════════
                  <form onSubmit={handleRfpSubmit} noValidate>
                    {apiError && (
                      <motion.div
                        className="mb-6 flex items-center gap-2 p-3 rounded-lg bg-red-500/15 border border-red-400/40"
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <AlertCircle className="w-4 h-4 text-red-200 flex-shrink-0" />
                        <p className="text-sm text-red-100">{apiError}</p>
                      </motion.div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                      {/* Row 1: Full name + Work email */}
                      <div>
                        <label htmlFor="connect-rfp-name" className="sr-only">
                          Full name
                        </label>
                        <input
                          type="text"
                          id="connect-rfp-name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Full name"
                          className={inputCls(errors.name)}
                          aria-invalid={Boolean(errors.name)}
                        />
                        {errors.name && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.name}
                          </p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="connect-rfp-workEmail" className="sr-only">
                          Work email
                        </label>
                        <input
                          type="email"
                          id="connect-rfp-workEmail"
                          name="workEmail"
                          value={formData.workEmail}
                          onChange={handleChange}
                          placeholder="Work email address"
                          className={inputCls(errors.workEmail)}
                          aria-invalid={Boolean(errors.workEmail)}
                        />
                        {errors.workEmail && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.workEmail}
                          </p>
                        )}
                      </div>

                      {/* Row 2: Company + Phone */}
                      <div>
                        <label htmlFor="connect-rfp-company" className="sr-only">
                          Company
                        </label>
                        <input
                          type="text"
                          id="connect-rfp-company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="Company name"
                          className={inputCls(errors.company)}
                          aria-invalid={Boolean(errors.company)}
                        />
                        {errors.company && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.company}
                          </p>
                        )}
                      </div>
                      <div>
                        <PhoneInput
                          id="connect-rfp-phone"
                          value={formData.phone}
                          onChange={handlePhoneChange}
                          hasError={Boolean(errors.phone)}
                        />
                        {errors.phone && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.phone}
                          </p>
                        )}
                      </div>

                      {/* Row 3: Multi-select relationship chips */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-white/85 mb-2">
                          Relationship{' '}
                          <span className="text-white/50 font-normal">
                            (select all that apply)
                          </span>
                        </label>
                        <div className="flex flex-wrap gap-2">
                          {RELATIONSHIP_OPTIONS.map((opt) => {
                            const selected = rfpRelationships.includes(opt.id);
                            return (
                              <button
                                key={opt.id}
                                type="button"
                                onClick={() => toggleRfpRelationship(opt.id)}
                                aria-pressed={selected}
                                className={cn(
                                  'inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium border transition-all',
                                  selected
                                    ? 'bg-accent-500 text-white border-accent-500 shadow-md'
                                    : 'bg-white/10 text-white/80 border-white/25 hover:bg-white/15 hover:border-white/40',
                                )}
                              >
                                <span>{opt.label}</span>
                                {selected && (
                                  <XIcon
                                    className="w-3.5 h-3.5 opacity-80"
                                    aria-hidden
                                  />
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {errors.rfpRelationships && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.rfpRelationships}
                          </p>
                        )}
                      </div>

                      {/* Row 4: RFP document upload */}
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-white/85 mb-2">
                          Upload RFP / RFI document *{' '}
                          <span className="text-white/50 font-normal">
                            (PDF only · max{' '}
                            {(MAX_RFP_BYTES / (1024 * 1024)).toFixed(0)} MB)
                          </span>
                        </label>
                        {rfpFile ? (
                          <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg border border-white/25 bg-white/10 backdrop-blur-sm">
                            <div className="flex items-center gap-3 min-w-0">
                              <FileText className="w-5 h-5 text-accent-300 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">
                                  {rfpFile.name}
                                </p>
                                <p className="text-xs text-white/60">
                                  {(rfpFile.size / 1024).toFixed(1)} KB
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={handleRemoveRfpFile}
                              className="p-1.5 rounded-md text-white/70 hover:text-red-300 hover:bg-red-500/15 transition-colors flex-shrink-0"
                              aria-label="Remove file"
                            >
                              <XIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <label
                            htmlFor="connect-rfp-attachment"
                            onDragOver={(e) => {
                              e.preventDefault();
                              setIsDraggingFile(true);
                            }}
                            onDragLeave={() => setIsDraggingFile(false)}
                            onDrop={handleRfpFileDrop}
                            className={cn(
                              'flex items-center gap-3 px-4 py-4 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
                              errors.rfpFile
                                ? 'border-red-400/60 bg-red-500/10'
                                : isDraggingFile
                                  ? 'border-accent-300/70 bg-white/15'
                                  : 'border-white/25 bg-white/5 hover:bg-white/10 hover:border-white/40',
                            )}
                          >
                            <Paperclip className="w-5 h-5 text-white/70 flex-shrink-0" />
                            <div className="text-sm text-white/80">
                              Drag and drop your file here or{' '}
                              <span className="font-semibold text-accent-200 underline underline-offset-2">
                                Browse
                              </span>
                              <span className="block text-xs text-white/50 mt-0.5">
                                PDF only · up to{' '}
                                {(MAX_RFP_BYTES / (1024 * 1024)).toFixed(0)} MB
                              </span>
                            </div>
                            <input
                              id="connect-rfp-attachment"
                              type="file"
                              accept={RFP_ACCEPT_ATTR}
                              onChange={handleRfpFileInput}
                              className="hidden"
                            />
                          </label>
                        )}
                        {errors.rfpFile && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.rfpFile}
                          </p>
                        )}
                      </div>

                      {/* Row 5: Industry (full width, optional) — same
                          catalog as the contact-us tab so a tab switch
                          preserves the visitor's selection cleanly. */}
                      <div className="md:col-span-2">
                        <label htmlFor="connect-rfp-industry" className="sr-only">
                          Industry
                        </label>
                        <select
                          id="connect-rfp-industry"
                          name="industry"
                          value={formData.industry}
                          onChange={handleChange}
                          className={`${inputCls(errors.industry)} appearance-none`}
                        >
                          <option value="" className={optionCls}>
                            Which industry are you in? (optional)
                          </option>
                          {INDUSTRY_OPTIONS.map((ind) => (
                            <option key={ind.id} value={ind.id} className={optionCls}>
                              {ind.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Row 6: Message (with mic + AI polish toolbar) */}
                      <div className="md:col-span-2">
                        <label htmlFor="connect-rfp-message" className="sr-only">
                          Message
                        </label>
                        <MessageAssistToolbar
                          value={formData.message}
                          onChange={setMessageValue}
                          context="rfp"
                          disabled={isSubmitting}
                        >
                          <textarea
                            id="connect-rfp-message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            rows={5}
                            placeholder="Summarize the scope, timelines, and any specific asks…"
                            className={`${inputCls(errors.message)} resize-none`}
                            style={{ paddingRight: '6rem' }}
                            aria-invalid={Boolean(errors.message)}
                          />
                        </MessageAssistToolbar>
                        {errors.message && (
                          <p className={errText}>
                            <AlertCircle className="w-3 h-3" />
                            {errors.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <p className="text-xs text-white/55">
                        We&rsquo;ll send a 4-digit code to your work email to
                        confirm your identity. Personal-provider emails (gmail,
                        yahoo…) aren&rsquo;t accepted.
                      </p>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        isLoading={isSubmitting}
                        rightIcon={<Upload className="w-5 h-5" />}
                        className="md:w-auto w-full"
                      >
                        Submit RFP
                      </Button>
                    </div>

                    {/* Privacy / compliance reassurance — same
                        wording as the contact-us tab. Extracted into
                        `<FormPrivacyNotice />` so the copy stays in
                        lock-step across every form on the site. */}
                    <FormPrivacyNotice tone="onDark" className="mt-6" />
                  </form>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </Container>
      </section>
    </>
  );
};
